import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Ship, Thermometer, Zap, Container, Anchor, Snowflake, Fuel } from "lucide-react";

const IMODCSCalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const coreCalculations = [
    {
      id: "reefer-consumption-sfoc",
      title: "Reefer Consumption (SFOC-based)",
      description: "Calculates electrical consumption for reefer containers using SFOC values",
      formula: "FC_electrical (MT) = (SFOC × KWh) ÷ 1,000,000",
      subFormulas: [
        "KWh = Container-days × Cx × 24",
        "Cx = 2.75 kW/h per reefer container",
        "Container-days = Sea-days + Port-days",
        "SFOC = 175 (Two Stroke) or 200 (Four Stroke)",
        "Units conversion: grams → metric tons"
      ],
      icon: <Container className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "vessel-specific-sfoc",
      title: "Vessel-Specific SFOC Determination",
      description: "Determines SFOC value based on vessel configuration and preferences",
      formula: "SFOC = f(defaultSFOCUsed, electricalEngineType, sfocUsed)",
      subFormulas: [
        "defaultSFOCUsed='YES': Use regulation values",
        "Two Stroke Engine: 175 g/kWh",
        "Four Stroke Engine: 200 g/kWh",
        "defaultSFOCUsed='NO': Use vessel.sfocUsed value",
        "Fallback: Regulation values if missing"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "container-days-calculation",
      title: "Container-Days Calculation",
      description: "Calculates total reefer container-days for consumption estimation",
      formula: "Total Container-days = Sea Container-days + Port Container-days",
      subFormulas: [
        "Sea Container-days = Sea-days × Number of Reefers",
        "Port Container-days = Port-days × Average Port Reefers",
        "Port Reefers = (portReefersArrival + portReefersDeparture) ÷ 2",
        "Shore Power: Excludes port consumption if used"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "vessel-type-specific-consumption",
      title: "Vessel-Type Specific Consumption",
      description: "Different consumption calculations based on vessel type",
      formula: "FC_electrical = f(vesselType, specializedConsumption)",
      subFormulas: [
        "Container ships: Reefer consumption only",
        "Gas/LNG carriers: Cooling system consumption",
        "Tankers: Discharge pump consumption",
        "Other vessels: Reefer consumption (general)",
        "Gas/LNG cooling: SFOC × coolingCargoKWh",
        "Tanker discharge: SFOC × dischargeCargoKWh"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    }
  ];

  const seaDaysCalculations = [
    {
      id: "sea-days-from-voyage",
      title: "Sea Days Calculation from Voyage",
      description: "Calculates sea days based on current voyage data",
      formula: "Sea-days = Voyage Duration (hours) ÷ 24",
      subFormulas: [
        "Find current voyage: departTime < reportDate < arrivalTime",
        "Active voyage: No arrivalTime (still at sea)",
        "Hours at Sea = (reportDate - departTime) ÷ (1000×60×60)",
        "timeTravelled field parsing (e.g., '48 hours')",
        "Voyage duration: arrivalTime - departTime"
      ],
      icon: <Anchor className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "voyage-lookup-logic",
      title: "Voyage Lookup Logic",
      description: "Finds the current voyage for sea days calculation",
      formula: "Current Voyage = find voyage where reportDate between departTime and arrivalTime",
      subFormulas: [
        "Query: departTime < reportDate AND arrivalTime > reportDate",
        "OR departTime < reportDate AND arrivalTime = null",
        "Sort by: departTime descending",
        "Select: departTime, arrivalTime, timeTravelled, distanceTravelled",
        "Fallback: Return 0 sea days if no voyage found"
      ],
      icon: <Anchor className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    }
  ];

  const boilerCalculations = [
    {
      id: "boiler-fuel-consumption",
      title: "Boiler Fuel Consumption",
      description: "Calculates boiler consumption for cargo operations",
      formula: "FC_boiler (MT) = Boiler Cargo Heating + Boiler Cargo Discharge",
      subFormulas: [
        "Direct fuel consumption (MT)",
        "Separate from electrical consumption",
        "Cargo heating: For certain vessel types",
        "Cargo discharge: For pumping operations",
        "From IMODCS record fields"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "other-consumption",
      title: "Other Consumption",
      description: "Miscellaneous electrical consumption calculations",
      formula: "FC_others (MT) = (SFOC × Discharge Cargo KWh) ÷ 1,000,000",
      subFormulas: [
        "Uses dischargeCargoKWh field",
        "Same SFOC value as other calculations",
        "For non-primary electrical loads",
        "Converted from grams to metric tons"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-yellow-200",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
    }
  ];

  const dataSources = [
    {
      collection: "IMODCS",
      fields: [
        "reportStartTime", "reportEndTime", "reportDate",
        "cargoQuantity", "distanceTravelledinLadenCondition",
        "mainEngineConsHFO/LFO/MGO", "auxEngineConsHFO/LFO/MGO",
        "boilerEngineConsHFO/LFO/MGO", "reeferkwh", "noReeferCont",
        "shorePowerUsed", "portDays", "portReefersArrival", "portReefersDeparture",
        "coolingCargo", "dischargeCargo", "boilerCargoHeating", "boilerCargoDischarge",
        "exceptionalConditions", "sailingInIceCondition", "STSOperation",
        "onShorePowerSupplied", "shorePowerEnergy", "hoursUnderway"
      ],
      purpose: "IMO DCS operational data"
    },
    {
      collection: "Vessel",
      fields: ["defaultSFOCUsed", "electricalEngineType", "sfocUsed", "vesselType"],
      purpose: "Vessel configuration and characteristics"
    },
    {
      collection: "VesselVoyage",
      fields: ["departTime", "arrivalTime", "timeTravelled", "distanceTravelled"],
      purpose: "Voyage data for sea days calculation"
    }
  ];

  const vesselTypeConfigurations = [
    { type: "Container ships", consumption: "Reefer containers only", sfoc: "Two Stroke=175, Four Stroke=200" },
    { type: "Gas carriers / LNG", consumption: "Cooling system + reefer", sfoc: "Based on engine type" },
    { type: "Chemical/Oil Tankers", consumption: "Discharge pumps + reefer", sfoc: "Based on engine type" },
    { type: "Other vessels", consumption: "Reefer containers (general)", sfoc: "Based on engine type" }
  ];

  const sfocValues = {
    regulation: {
      "Two Stroke": 175,
      "Four Stroke": 200
    },
    factors: {
      "containerDayFactor": 2.75, // Cx = kW/h per reefer container
      "hoursPerDay": 24,
      "conversionToMT": 1000000 // grams to metric tons
    }
  };

  const operationalConditions = [
    { condition: "Exceptional Conditions", field: "exceptionalConditions", impact: "May affect consumption calculations" },
    { condition: "Sailing in Ice", field: "sailingInIceCondition", impact: "Increased fuel consumption" },
    { condition: "STS Operations", field: "STSOperation", impact: "Ship-to-ship transfer operations" },
    { condition: "Shore Power", field: "onShorePowerSupplied", impact: "Reduces onboard electrical consumption" }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fad8d4] rounded-lg">
              <Calculator className="w-6 h-6 text-[#a93226]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                IMO DCS Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                IMO Data Collection System fuel consumption and transport work methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Data Sources</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    <div className="text-sm text-gray-600 mb-2">{source.purpose}</div>
                    <div className="text-xs text-gray-500 space-y-1">
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">{field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Vessel Type Configurations</h3>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {vesselTypeConfigurations.map((config, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div className="font-semibold text-gray-800 text-sm">{config.type}</div>
                      <div className="text-xs text-gray-600 mt-1">{config.consumption}</div>
                      <div className="text-xs text-blue-600 font-mono mt-1">{config.sfoc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">SFOC Configuration & Constants</h3>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="font-medium text-green-800 mb-2">Regulation SFOC Values (g/kWh):</div>
                    <div className="space-y-2">
                      {Object.entries(sfocValues.regulation).map(([engine, value]) => (
                        <div key={engine} className="flex justify-between items-center">
                          <span className="text-sm text-green-700">{engine}:</span>
                          <span className="font-mono font-bold text-green-800">{value} g/kWh</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-green-800 mb-2">Conversion Factors:</div>
                    <div className="space-y-2">
                      {Object.entries(sfocValues.factors).map(([factor, value]) => (
                        <div key={factor} className="flex justify-between items-center">
                          <span className="text-sm text-green-700">{factor}:</span>
                          <span className="font-mono text-green-800">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Operational Conditions</h3>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {operationalConditions.map((condition, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div className="font-medium text-gray-800">{condition.condition}</div>
                      <div className="text-xs text-gray-600">Field: {condition.field}</div>
                      <div className="text-xs text-amber-600 mt-1">{condition.impact}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Sea Days Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {seaDaysCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Boiler & Other Consumption</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {boilerCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Complete Electrical Consumption</h4>
              <div className="text-sm text-red-700 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 1: SFOC Determination</div>
                    <div className="text-xs font-mono mt-1">SFOC = 175 (Two Stroke)</div>
                    <div className="text-xs text-red-600">defaultSFOCUsed='YES', electricalEngineType='Two Stroke'</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 2: Container-Days</div>
                    <div className="text-xs font-mono mt-1">10 reefers × 5 sea-days = 50 container-days</div>
                    <div className="text-xs text-red-600">+ 20 port container-days = 70 total</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 3: KWh Calculation</div>
                    <div className="text-xs font-mono mt-1">70 × 2.75 × 24 = 4,620 KWh</div>
                    <div className="text-xs text-red-600">Cx = 2.75 kW/h per container</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 4: Fuel Mass</div>
                    <div className="text-xs font-mono mt-1">(175 × 4,620) ÷ 1,000,000 = 0.8085 MT</div>
                    <div className="text-xs text-red-600">Final electrical consumption</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IMODCSCalculations;